import React from "react";
import { useSessionTimer } from "../hooks/useSessionTimer.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useNavigate } from "react-router-dom";

/**
 * Componente que muestra el tiempo restante de sesión y maneja la renovación automática.
 * Se muestra en amarillo cuando queda menos de 10 minutos y en rojo cuando queda menos de 2 minutos.
 * Muestra indicador de inactividad cuando el usuario no ha interactuado recientemente.
 */
export default function SessionTimer() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("agent_token");
    setUser(null);
    navigate("/login", { replace: true });
  };

  const { remainingSeconds, formattedTime, refresh, isRefreshing, isActive } = useSessionTimer({
    onLogout: handleLogout,
    refreshBeforeSeconds: 300, // Renovar 5 minutos antes de expirar
    inactivityThresholdSeconds: 900 // 15 minutos de inactividad
  });

  if (remainingSeconds === null) {
    return null;
  }

  // Solo mostrar warning/critical si el usuario está inactivo (no se renovará)
  const getStatusClass = () => {
    if (!isActive) {
      if (remainingSeconds <= 120) return "session-timer--critical";
      if (remainingSeconds <= 600) return "session-timer--warning";
    }
    return ""; // Gris por defecto
  };

  const getTitle = () => {
    if (isActive) return "Sesión activa - se renovará automáticamente";
    if (remainingSeconds <= 120) return "¡Sesión por expirar! Hacé clic para renovar";
    if (remainingSeconds <= 600) return "Inactivo - la sesión no se renovará automáticamente";
    return "Inactivo";
  };

  return (
    <div className={`session-timer ${getStatusClass()}`} title={getTitle()}>
      <span className="session-timer__icon">{isActive ? "⏱" : "💤"}</span>
      <span className="session-timer__time">{formattedTime}</span>
      {!isActive && (
        <button 
          className="session-timer__refresh"
          onClick={refresh}
          disabled={isRefreshing}
          title="Renovar sesión"
        >
          {isRefreshing ? "..." : "🔄"}
        </button>
      )}
    </div>
  );
}
